import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Text, FlexContainer, Spinner } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { userStageSelector } from 'Store/modules/validations/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { useFetchPreviousYearCacheInfo, useGetUserWithPreFiling } from 'Containers/Dashboard/hooks';
import Arrow from 'Components/icons/Arrow';
import { stagesLabels } from 'Constants/stages';
import { TAX_YEAR } from 'Config';
import PreviousYearBanner from '../PreviousYearBanner';
import { TaxesListProps } from './types';
import * as Styled from './styles';

const TaxesList = ({ taxes, activeTax, onClick, isLoading }: TaxesListProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.DASHBOARD);
  const userStage = useSelector(userStageSelector);
  const currentTax = useMemo(
    () => taxes?.find(tax => tax.taxYear?.toString() === activeTax?.toString()),
    [taxes, activeTax]
  );
  const isUserWithPreFiling = useGetUserWithPreFiling(userStage, currentTax?.taxReturnResult);
  const data = useFetchPreviousYearCacheInfo();

  return (
    <Styled.Content>
      {isLoading ? (
        <FlexContainer justify="center" fullWidth>
          <Spinner size="M" justify="center" align="center" mode="primary" />
        </FlexContainer>
      ) : (
        <>
          {taxes
            ?.filter(item => item.taxYear === TAX_YEAR)
            ?.map(({ recordId, taxYear, stage }) => (
              <Styled.TaxBox
                onClick={() => onClick(recordId, taxYear)}
                isActive={taxYear?.toString() === activeTax?.toString()}
                key={taxYear}
                stage={stage}
                $isUserWithPreFiling={isUserWithPreFiling}
              >
                <FlexContainer justify="space-between" align="center" gap="dimension_08">
                  <FlexContainer direction="column">
                    <Text size="S_L_S" type="headline" color="dark">
                      {taxYear}
                    </Text>
                    <Text size="S_L_L" type="paragraphs" color="dark">
                      {isUserWithPreFiling
                        ? t('Dashboard.status.notMandatory')
                        : (t(stagesLabels?.[stage]) ?? t('Dashboard.status.inProgress'))}
                    </Text>
                  </FlexContainer>
                  <Arrow />
                </FlexContainer>
              </Styled.TaxBox>
            ))}
          <PreviousYearBanner data={data} isUserWithPreFiling={isUserWithPreFiling} />
        </>
      )}
    </Styled.Content>
  );
};

export default TaxesList;
